import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { procApi } from '../../api/procurement.js';

export default function Rfqs() {
  const { id } = useParams();
  if (id === 'new') return <NewRfq />;
  if (id) return <RfqDetail id={id} />;
  return <RfqList />;
}

function RfqList() {
  const [items, setItems] = useState([]);
  useEffect(() => { procApi.listRFQs().then((d) => setItems(d?.items || [])); }, []);
  return (
    <section>
      <header className="page-header">
        <h1>Requests for Quotation</h1>
        <Link to="/procurement/rfqs/new" className="btn primary">+ New RFQ</Link>
      </header>
      <div className="card">
        <table className="data-table">
          <thead><tr><th>RFQ #</th><th>From PR</th><th>Issued</th><th>Deadline</th><th>Status</th></tr></thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id}>
                <td><Link to={`/procurement/rfqs/${r.id}`}><code>{r.rfq_no}</code></Link></td>
                <td className="muted">{r.request_no || '—'}</td>
                <td>{r.issued_date || '—'}</td>
                <td>{r.deadline}</td>
                <td><span className={`chip status-${r.status === 'sent' ? 'pending' : r.status}`}>{r.status}</span></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="muted">No RFQs yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function NewRfq() {
  const nav = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [prs, setPrs] = useState([]);
  const [vendorIds, setVendorIds] = useState([]);
  const [prId, setPrId] = useState('');
  const [deadline, setDeadline] = useState(new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch((import.meta.env.VITE_API_BASE_URL || '/api') + '/vendors?active=true', {
      headers: { Authorization: `Bearer ${localStorage.getItem('bos.access')}` },
    }).then((r) => r.json()).then((j) => setVendors(j?.data?.items || []));
    procApi.listPRs({ status: 'approved' }).then((d) => setPrs(d?.items || []));
  }, []);

  function toggleVendor(id) {
    setVendorIds((vs) => vs.includes(id) ? vs.filter((x) => x !== id) : [...vs, id]);
  }

  async function submit(e) {
    e.preventDefault();
    setError(null);
    if (!vendorIds.length) { setError('Select at least one vendor'); return; }
    try {
      const created = await procApi.createRFQ({ prId: prId || null, deadline, notes: notes || null, vendorIds });
      nav(`/procurement/rfqs/${created.rfq.id}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    }
  }

  return (
    <section>
      <header className="page-header"><h1>New RFQ</h1></header>
      {error && <div className="alert error">{error}</div>}
      <form onSubmit={submit} className="card form-card">
        <div className="grid-form">
          <label>Linked PR (optional)
            <select value={prId} onChange={(e) => setPrId(e.target.value)}>
              <option value="">—</option>
              {prs.map((p) => <option key={p.id} value={p.id}>{p.request_no} · {Number(p.total_estimate).toFixed(2)}</option>)}
            </select>
          </label>
          <label>Deadline<input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} required /></label>
          <label className="full">Notes<textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
        </div>
        <h3>Invite Vendors</h3>
        <div className="vendor-grid">
          {vendors.map((v) => (
            <label key={v.id} className={`vendor-pick ${vendorIds.includes(v.id) ? 'picked' : ''}`}>
              <input type="checkbox" checked={vendorIds.includes(v.id)} onChange={() => toggleVendor(v.id)} />
              <span>{v.name}</span>
            </label>
          ))}
          {vendors.length === 0 && <p className="muted">No vendors yet — add some in Finance → Vendors.</p>}
        </div>
        <div className="actions">
          <button type="submit" className="btn primary">Create RFQ</button>
        </div>
      </form>
    </section>
  );
}

function RfqDetail({ id }) {
  const [data, setData] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('vendors');

  function refresh() {
    procApi.getRFQ(id).then((d) => setData(d?.rfq)).catch((e) => setError(e.response?.data?.message));
    procApi.rfqComparison(id).then((d) => setComparison(d?.comparison || [])).catch(() => {});
  }
  useEffect(refresh, [id]);

  async function send() {
    try { await procApi.sendRfqInvitations(id); refresh(); }
    catch (e) { setError(e.response?.data?.message); }
  }

  async function recordResponse(rfqVendorId) {
    const text = window.prompt('Paste lines as JSON: [{description, quantity, unitPrice}, ...]');
    if (!text) return;
    let items;
    try { items = JSON.parse(text); }
    catch { alert('Invalid JSON'); return; }
    const days = Number(window.prompt('Delivery days?', '14')) || 14;
    try {
      await procApi.submitRfqResponse(rfqVendorId, { items, deliveryDays: days });
      refresh();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed');
    }
  }

  if (error) return <div className="alert error">{error}</div>;
  if (!data) return <div className="muted">Loading…</div>;

  return (
    <section>
      <header className="page-header">
        <div>
          <h1>{data.rfq_no}</h1>
          <p className="muted">Deadline {data.deadline} · <span className={`chip status-${data.status === 'sent' ? 'pending' : data.status}`}>{data.status}</span></p>
        </div>
        <div className="actions">
          <Link to="/procurement/rfqs" className="btn ghost">← All RFQs</Link>
          {data.status === 'draft' && <button className="btn primary" onClick={send}>Send Invitations</button>}
        </div>
      </header>

      <div className="tabs">
        <button className={tab === 'vendors' ? 'tab active' : 'tab'} onClick={() => setTab('vendors')}>Vendors ({data.vendors.length})</button>
        <button className={tab === 'comparison' ? 'tab active' : 'tab'} onClick={() => setTab('comparison')}>Comparison ({comparison?.length || 0})</button>
      </div>

      {tab === 'vendors' && (
        <div className="card">
          <table className="data-table">
            <thead><tr><th>Vendor</th><th>Status</th><th>Total</th><th>Delivery</th><th>Submitted</th><th></th></tr></thead>
            <tbody>
              {data.vendors.map((v) => (
                <tr key={v.rfq_vendor_id}>
                  <td><strong>{v.vendor_name}</strong></td>
                  <td><span className={`chip status-${v.vendor_status === 'invited' ? 'pending' : v.vendor_status === 'responded' ? 'approved' : 'cancelled'}`}>{v.vendor_status}</span></td>
                  <td>{v.total_amount ? Number(v.total_amount).toFixed(2) : '—'}</td>
                  <td>{v.delivery_days ? `${v.delivery_days} days` : '—'}</td>
                  <td className="muted">{v.submitted_at?.slice(0, 16).replace('T', ' ') || '—'}</td>
                  <td>{!v.response_id && <button className="btn ghost small" onClick={() => recordResponse(v.rfq_vendor_id)}>Record Response</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'comparison' && (
        <div className="card">
          {!comparison || comparison.length === 0 ? (
            <div className="muted">No vendor responses yet.</div>
          ) : (
            <>
              <table className="data-table comparison">
                <thead><tr><th>Vendor</th><th>Total</th><th>Delivery</th><th>Price</th><th>Delivery</th><th>Quality</th><th>Overall</th><th></th></tr></thead>
                <tbody>
                  {comparison.map((c, i) => (
                    <tr key={c.rfqVendorId} className={i === 0 ? 'winner' : ''}>
                      <td><strong>{c.vendorName}</strong>{i === 0 && <span className="chip status-approved">Best</span>}</td>
                      <td>{c.total.toFixed(2)}</td>
                      <td>{c.deliveryDays || '—'} days</td>
                      <td><ScoreBar value={c.priceScore} /></td>
                      <td><ScoreBar value={c.deliveryScore} /></td>
                      <td><ScoreBar value={c.qualityScore} /></td>
                      <td><strong>{c.overallScore.toFixed(1)}</strong></td>
                      <td>
                        <Link to={`/procurement/orders/new?rfq=${id}&vendor=${c.vendorId}`} className="btn primary small">Create PO</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </section>
  );
}

function ScoreBar({ value }) {
  return (
    <div className="score-bar">
      <div className="score-fill" style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
      <span>{Number(value).toFixed(0)}</span>
    </div>
  );
}
